import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { apiClient, API, formatApiErrorDetail } from "@/lib/api";
import { AdminGuard } from "@/components/admin/AdminShell";
import { PaginationBar } from "@/components/admin/ListControls";
import { usePagedList } from "@/hooks/usePagedList";
import { INVOICE_STATUS_LABELS, euros, formatDate } from "@/constants/business_status";

function Badge({ children }) {
  return <span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1 whitespace-nowrap">{children}</span>;
}

export function PaymentsPage() {
  const list = usePagedList("/admin/payments", {}, []);
  const [form, setForm] = useState({ case_id: "", amount: "", method: "virement", kind: "deposit", reference: "" });
  const [cases, setCases] = useState([]);
  useEffect(() => {
    apiClient.get("/admin/cases", { params: { page_size: 100 } }).then((r) => setCases(r.data.items)).catch(() => {});
  }, []);
  const create = async () => {
    if (!form.case_id || !form.amount) return toast.error("Dossier et montant requis");
    try {
      await apiClient.post("/admin/payments", {
        case_id: form.case_id, kind: form.kind, method: form.method,
        amount_cents: Math.round(parseFloat(form.amount) * 100), reference: form.reference || null });
      toast.success("Paiement enregistré"); list.refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const act = async (id, action) => {
    try { await apiClient.post(`/admin/payments/${id}/${action}`, {}); toast.success("Fait"); list.refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const STATUS_FR = { recorded: "Enregistré", pending_verification: "À vérifier", verified: "Vérifié", rejected: "Refusé", cancelled: "Annulé" };
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-1">Paiements manuels</h1>
      <p className="text-sm text-muted-foreground mb-4">Virement, espèces, chèque — aucun paiement en ligne.</p>
      <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap gap-2 items-end" data-testid="payment-form">
        <div className="min-w-[200px]"><label className="text-[11px] font-semibold text-navy">Dossier</label>
          <Select value={form.case_id} onValueChange={(v) => setForm((p) => ({ ...p, case_id: v }))}>
            <SelectTrigger data-testid="payment-case" className="h-9 bg-white rounded-lg"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.contact_name || ""}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-[11px] font-semibold text-navy">Montant (€)</label>
          <Input data-testid="payment-amount" type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} className="h-9 w-28 bg-white rounded-lg" /></div>
        <div><label className="text-[11px] font-semibold text-navy">Type</label>
          <Select value={form.kind} onValueChange={(v) => setForm((p) => ({ ...p, kind: v }))}>
            <SelectTrigger data-testid="payment-kind" className="h-9 w-32 bg-white rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="deposit">Acompte</SelectItem><SelectItem value="balance">Solde</SelectItem><SelectItem value="installment">Échéance</SelectItem><SelectItem value="refund">Remboursement</SelectItem></SelectContent>
          </Select></div>
        <div><label className="text-[11px] font-semibold text-navy">Mode</label>
          <Select value={form.method} onValueChange={(v) => setForm((p) => ({ ...p, method: v }))}>
            <SelectTrigger data-testid="payment-method" className="h-9 w-32 bg-white rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="virement">Virement</SelectItem><SelectItem value="especes">Espèces</SelectItem><SelectItem value="cheque">Chèque</SelectItem><SelectItem value="autre">Autre</SelectItem></SelectContent>
          </Select></div>
        <div><label className="text-[11px] font-semibold text-navy">Référence</label>
          <Input data-testid="payment-reference" value={form.reference} onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))} className="h-9 w-32 bg-white rounded-lg" /></div>
        <Button data-testid="payment-submit" onClick={create} className="bg-orange hover:bg-orange/90 text-white rounded-lg h-9"><Plus className="w-4 h-4 mr-1" /> Enregistrer</Button>
      </div>
      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
        <Table data-testid="payments-table">
          <TableHeader><TableRow><TableHead>Dossier</TableHead><TableHead>Type</TableHead><TableHead>Montant</TableHead><TableHead>Mode</TableHead><TableHead>Référence</TableHead><TableHead>Date</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.loading ? <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy" /></TableCell></TableRow>
              : list.items.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Aucun paiement enregistré</TableCell></TableRow>
              : list.items.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TableCell className="text-xs font-semibold text-navy">{p.case_number || "—"}</TableCell>
                  <TableCell className="text-xs">{p.kind}</TableCell>
                  <TableCell className="font-semibold text-sm">{euros(p.amount_cents)}</TableCell>
                  <TableCell className="text-xs">{p.method}</TableCell>
                  <TableCell className="text-xs">{p.reference || "—"}</TableCell>
                  <TableCell className="text-xs">{formatDate(p.date || p.created_at)}</TableCell>
                  <TableCell><Badge>{STATUS_FR[p.status] || p.status}</Badge></TableCell>
                  <TableCell className="whitespace-nowrap">
                    {(p.status === "recorded" || p.status === "pending_verification") && (
                      <>
                        <Button data-testid={`payment-verify-${p.id}`} size="sm" variant="ghost" className="text-green-700 h-8 px-2" onClick={() => act(p.id, "verify")}><Check className="w-4 h-4" /></Button>
                        <Button data-testid={`payment-reject-${p.id}`} size="sm" variant="ghost" className="text-red-600 h-8 px-2" onClick={() => act(p.id, "reject")}><X className="w-4 h-4" /></Button>
                      </>
                    )}
                    {p.status !== "cancelled" && <Button data-testid={`payment-cancel-${p.id}`} size="sm" variant="ghost" className="text-xs h-8" onClick={() => act(p.id, "cancel")}>Annuler</Button>}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      <PaginationBar total={list.total} page={list.page} pageSize={list.pageSize} totalPages={list.total_pages} onPage={list.setPage} onSize={list.setPageSize} loading={list.loading} testPrefix="payments-" />
    </AdminGuard>
  );
}

export function InvoicesPage() {
  const list = usePagedList("/admin/invoices", {}, []);
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState("");
  useEffect(() => {
    apiClient.get("/admin/cases", { params: { page_size: 100, status: "accepted" } }).then((r) => setCases(r.data.items)).catch(() => {});
  }, []);
  const create = async () => {
    if (!caseId) return toast.error("Choisissez un dossier");
    try {
      const quotes = await apiClient.get("/admin/quotes", { params: { case_id: caseId, status: "accepted" } });
      const q = quotes.data.items[0];
      await apiClient.post("/admin/invoices", { case_id: caseId, from_quote_id: q?.id || null });
      toast.success("Facture brouillon créée"); list.refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const issue = async (id) => {
    try { await apiClient.post(`/admin/invoices/${id}/issue`); toast.success("Facture émise"); list.refresh(); }
    catch (e) {
      const det = e.response?.data?.detail;
      toast.error(det?.missing ? `${det.message} : ${det.missing.join(", ")}` : formatApiErrorDetail(det));
    }
  };
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Factures</h1>
      <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap gap-2 items-end" data-testid="invoice-form">
        <div className="min-w-[240px]"><label className="text-[11px] font-semibold text-navy">Dossier accepté à facturer</label>
          <Select value={caseId} onValueChange={setCaseId}>
            <SelectTrigger data-testid="invoice-case" className="h-9 bg-white rounded-lg"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.contact_name || ""}</SelectItem>)}</SelectContent>
          </Select></div>
        <Button data-testid="invoice-create" onClick={create} className="bg-orange hover:bg-orange/90 text-white rounded-lg h-9"><Plus className="w-4 h-4 mr-1" /> Créer depuis le devis accepté</Button>
      </div>
      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
        <Table data-testid="invoices-table">
          <TableHeader><TableRow><TableHead>Numéro</TableHead><TableHead>Dossier</TableHead><TableHead>Total TTC</TableHead><TableHead>Échéance</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.loading ? <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy" /></TableCell></TableRow>
              : list.items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Aucune facture</TableCell></TableRow>
              : list.items.map((i) => (
                <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
                  <TableCell className="text-xs font-semibold text-navy">{i.invoice_number || "Brouillon"}{i.is_credit_note ? " (avoir)" : ""}</TableCell>
                  <TableCell className="text-xs">{i.case_number}</TableCell>
                  <TableCell className="font-semibold text-sm">{euros(i.totals?.total_ttc_cents)}</TableCell>
                  <TableCell className="text-xs">{i.due_date || "—"}</TableCell>
                  <TableCell><Badge>{INVOICE_STATUS_LABELS[i.status] || i.status}</Badge></TableCell>
                  <TableCell className="whitespace-nowrap">
                    <a href={`${API}/admin/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="text-orange text-xs font-semibold underline mr-3" data-testid={`invoice-pdf-${i.id}`}>PDF</a>
                    {i.status === "draft" && <Button data-testid={`invoice-issue-${i.id}`} size="sm" variant="outline" className="text-xs rounded-lg h-8" onClick={() => issue(i.id)}>Émettre</Button>}
                    {i.status === "issued" && !i.is_credit_note && (
                      <>
                        <Button data-testid={`invoice-paid-${i.id}`} size="sm" variant="ghost" className="text-xs h-8 text-green-700" onClick={async () => { await apiClient.post(`/admin/invoices/${i.id}/mark-paid`); list.refresh(); }}>Réglée</Button>
                        <Button data-testid={`invoice-credit-${i.id}`} size="sm" variant="ghost" className="text-xs h-8" onClick={async () => { if (window.confirm("Émettre un avoir ?")) { await apiClient.post(`/admin/invoices/${i.id}/credit-note`); list.refresh(); } }}>Avoir</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      <PaginationBar total={list.total} page={list.page} pageSize={list.pageSize} totalPages={list.total_pages} onPage={list.setPage} onSize={list.setPageSize} loading={list.loading} testPrefix="invoices-" />
    </AdminGuard>
  );
}

export function DocumentsPage() {
  const list = usePagedList("/admin/documents", {}, []);
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Documents</h1>
      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
        <Table data-testid="documents-table">
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Type</TableHead><TableHead>Visibilité</TableHead><TableHead>Ajouté par</TableHead><TableHead>Date</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.loading ? <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy" /></TableCell></TableRow>
              : list.items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Aucun document</TableCell></TableRow>
              : list.items.map((d) => (
                <TableRow key={d.id} data-testid={`document-row-${d.id}`}>
                  <TableCell className="text-sm font-medium max-w-xs truncate">{d.name}</TableCell>
                  <TableCell><Badge>{d.doc_type}</Badge></TableCell>
                  <TableCell className="text-xs">{d.visibility === "client" ? "Client" : "Interne"}</TableCell>
                  <TableCell className="text-xs">{d.uploaded_by || "—"}</TableCell>
                  <TableCell className="text-xs">{formatDate(d.created_at)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <a href={`${API}/admin/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-orange text-xs font-semibold underline mr-2" data-testid={`document-download-${d.id}`}>Ouvrir</a>
                    <Button size="sm" variant="ghost" className="text-xs h-8" data-testid={`document-visibility-${d.id}`}
                      onClick={async () => { await apiClient.post(`/admin/documents/${d.id}/visibility`, { visibility: d.visibility === "client" ? "internal" : "client" }); list.refresh(); }}>
                      {d.visibility === "client" ? "Rendre interne" : "Partager au client"}</Button>
                    <Button size="sm" variant="ghost" className="text-xs h-8 text-red-600" data-testid={`document-archive-${d.id}`}
                      onClick={async () => { await apiClient.post(`/admin/documents/${d.id}/archive`); list.refresh(); }}>Archiver</Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      <PaginationBar total={list.total} page={list.page} pageSize={list.pageSize} totalPages={list.total_pages} onPage={list.setPage} onSize={list.setPageSize} loading={list.loading} testPrefix="documents-" />
    </AdminGuard>
  );
}
